// src/app/posts/[location]/[category]/[postId]/ClientPage.tsx
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { normalizeSlug } from '@/lib/seo/slug';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useSession } from 'next-auth/react';

import DetailToolbar from '@/components/common/DetailToolbar';
import MiniTalentCard from '@/components/common/MiniTalentCard';
import PostCard, { PostCardProps } from '@/components/PostCard';
import PostCardSkeleton from '@/components/skeletons/PostCard';
import CommentsPanelClient from '@/components/CommentsPanelClient';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ProfileCTAs from '@/components/profile/ProfileCTAs';
import { cn } from '@/lib/utils';
import { MapPin, Briefcase, Megaphone, Users, Sparkles, ChevronLeft, CalendarDays } from 'lucide-react';
import { toAbsoluteMediaUrl } from '@/lib/media';
import BlurGate from '@/components/common/BlurGate';
import { API_BASE as API_BASE_RAW } from '@/lib/apiBase';
import { getApiToken as getApiTokenAsync } from '@/lib/token';
import { sendMessage } from '@/lib/chatApi';
import { extractUserIdFromToken } from '@/lib/profile-helpers';
import { buildProfileLink } from '@/lib/chatUi';
import { apiPost } from '@/lib/api';
import ArticleSchema from '@/components/seo/ArticleSchema';
import { jsonLdHtml } from '@/lib/seo/jsonLd';
import { stripHtml, truncateDescription } from '@/lib/seo';
import { scrubContactInfo, CONTACT_MASK } from '@/lib/scrubContact';
import { escapeHtml, sanitizeHtml } from '@/lib/sanitizeHtml';
import { bucketForCategory, humanizeSlug, friendlyCity, postedDate } from '@/lib/posts/labels';
import { adminFetch } from '@/lib/adminFetch';

const API_BASE = API_BASE_RAW.replace(/\/+$/, '');

function timeAgo(input?: string | Date | null): string | null {
  if (!input) return null;
  const t = typeof input === 'string' ? Date.parse(input) : input.getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

function formatRenewDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return 'a later date';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

const BUCKET_THEME: Record<string, { label: string; Icon: React.ComponentType<{ className?: string }>; iconBg: string; iconColor: string; glow: string }> = {
  hiring:   { label: 'Now hiring', Icon: Briefcase, iconBg: 'bg-amber-500/10',  iconColor: 'text-amber-500',  glow: 'bg-amber-500/10' },
  // This row said 'Now hiring' with the Briefcase until 2026-09-01, so an
  // available-* post kept a NOW HIRING eyebrow even once the resolver stopped
  // collapsing looking into hiring. Silently, with nothing failing.
  looking:  { label: 'Available for work', Icon: Megaphone, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-500', glow: 'bg-emerald-500/10' },
  connect:  { label: 'Collaborate', Icon: Users,    iconBg: 'bg-pink-500/10',   iconColor: 'text-pink-500',   glow: 'bg-fuchsia-500/10' },
  services: { label: 'Services',   Icon: Sparkles, iconBg: 'bg-violet-500/10', iconColor: 'text-violet-400', glow: 'bg-violet-500/10' },
};

// initialPost is the server's anonymous copy of the post, passed only when the
// request carries no session cookie (guests and crawlers), so the first HTML
// already holds the title and body. canonicalUrl is the post's one address,
// slug included, for the structured data. Without them the page loads the post
// in the browser, as it always has.
export default function PostDetailPage({ initialPost = null, canonicalUrl = null }: { initialPost?: any | null; canonicalUrl?: string | null } = {}) {
  const { location, category, postId } = useParams() as { location: string; category: string; postId: string };
  const { data: session, status } = useSession();
  const isGuest = status === 'unauthenticated';
  const isAdmin = Boolean((session as any)?.user?.role === 'admin' || (session as any)?.user?.isAdmin === true);
  const guest = isGuest;

  const [post, setPost] = useState<any | null>(initialPost);
  const [related, setRelated] = useState<PostCardProps[] | null>(null);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [loading, setLoading] = useState(!initialPost);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminMsg, setAdminMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [postDeleted, setPostDeleted] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewNote, setRenewNote] = useState<{ text: string; ok: boolean } | null>(null);

  // Owner check for published posts: the session id is the auth GUID decoded
  // from the API JWT, and the detail DTO carries the author's auth GUID.
  const isOwner = useMemo(() => {
    // session.user.id is the EMAIL (NextAuth authorize returns id: email);
    // the author identity on the DTO is the AspNetUsers GUID, so decode the
    // GUID from the API access token instead.
    const at = (session as any)?.user?.accessToken as string | undefined;
    const mine = String(extractUserIdFromToken(at) || '').toLowerCase();
    const theirs = String((post as any)?.authorUserId || (post as any)?.AuthorUserId || '').toLowerCase();
    return Boolean(mine && theirs && mine === theirs);
  }, [session, post]);

  async function renewPost() {
    if (renewBusy) return;
    setRenewBusy(true);
    setRenewNote(null);
    try {
      await apiPost(`/api/post/${postId}/renew`, session);
      setRenewNote({ text: 'Renewed. Your post is back at the top of its listings.', ok: true });
    } catch (e: any) {
      const next = e?.problem?.nextRenewAt ?? e?.problem?.NextRenewAt ?? null;
      if (e?.status === 400 && next) {
        setRenewNote({ text: `Renewable again on ${formatRenewDate(String(next))}.`, ok: false });
      } else {
        setRenewNote({ text: 'Could not renew right now. Try again later.', ok: false });
      }
    } finally { setRenewBusy(false); }
  }

  async function adminDeactivate() {
    if (!post) return;
    const id = post.postId ?? post.PostId ?? postId;
    if (!window.confirm(`Deactivate post #${id}?`)) return;
    setAdminBusy(true); setAdminMsg(null);
    try {
      await adminFetch(`/api/proxy/api/admin/posts/${id}`, {
        method: 'PUT',
        body: { isActive: false },
      });
      setAdminMsg({ text: 'Post deactivated', ok: true });
      setPost((prev: any) => prev ? { ...prev, isActive: false } : prev);
    } catch (e: any) {
      setAdminMsg({ text: e?.message || 'Deactivate failed', ok: false });
    } finally { setAdminBusy(false); }
  }

  async function adminDelete() {
    if (!post) return;
    const id = post.postId ?? post.PostId ?? postId;
    const typed = window.prompt('Type DELETE to confirm permanently deleting this post:');
    if (typed !== 'DELETE') return;
    setAdminBusy(true); setAdminMsg(null);
    try {
      await adminFetch(`/api/proxy/api/admin/posts/${id}`, { method: 'DELETE', confirmWord: 'DELETE' });
      setAdminMsg({ text: 'Post deleted', ok: true });
      setPostDeleted(true);
    } catch (e: any) {
      setAdminMsg({ text: e?.message || 'Delete failed', ok: false });
    } finally { setAdminBusy(false); }
  }

  function linkify(text?: string): string {
    if (!text) return '';
    // Escape FIRST so any HTML in the user's text is inert; then add our own
    // trusted anchor markup. This is the XSS boundary for the Details field.
    let s = escapeHtml(text);
    s = s.replace(/(https?:\/\/[^\s<]+)/gi, '<a href="$1" target="_blank" rel="noopener">$1</a>');
    s = s.replace(/(^|\s)(www\.[^\s<]+)/gi, '$1<a href="https://$2" target="_blank" rel="noopener">$2</a>');
    // No tel: links: phone numbers are masked for every viewer, so one could never carry a number,
    // and the replacement nested an anchor inside a URL's href when a long id held ten digits.
    return s;
  }


  // With a server copy the page stays on screen while this refetch runs: no
  // skeleton, and a transient failure keeps the copy. A 404 or 410 still means
  // the post is gone.
  const hasInitialPost = initialPost != null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasInitialPost) setLoading(true);
      try {
        // Use same-origin Next API proxy to avoid env/CORS drift in prod
        const authHeaders: Record<string, string> = {};
        try { const tk = await getApiTokenAsync(); if (tk) authHeaders['Authorization'] = `Bearer ${tk}`; } catch {}
        const res = await fetch(`/api/post/${postId}`, { cache: 'no-store', headers: authHeaders });
        const data = res.ok ? await res.json() : null;
        if (!cancelled && (data || !hasInitialPost || res.status === 404 || res.status === 410)) setPost(data);

        // Track post view
        if (res.ok && data) {
          fetch(`/api/proxy/api/post/${postId}/view`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }).catch(() => {}); // Fire and forget
        }

        const r = await fetch(
          `/api/post/related?locationSlug=${encodeURIComponent(String(location))}&categorySlug=${encodeURIComponent(String(category))}&excludeId=${encodeURIComponent(String(postId))}&limit=8`,
          { cache: 'no-store', headers: authHeaders }
        );
        if (!cancelled) setRelated(r.ok ? await r.json() : []);

        // Resolve the human category name (e.g. w4m -> "Women for Men") for the eyebrow line.
        try {
          const cr = await fetch(`/api/categories?metro=${encodeURIComponent(String(location))}`, { cache: 'no-store' });
          if (cr.ok) {
            const cats: Array<{ slug: string; name: string }> = await cr.json();
            const match = Array.isArray(cats)
              ? cats.find(c => String(c.slug || '').toLowerCase() === String(category).toLowerCase())
              : null;
            if (!cancelled && match?.name) setCategoryName(match.name);
          }
        } catch {}
      } catch {
        if (!cancelled) {
          if (!hasInitialPost) setPost(null);
          setRelated([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location, category, postId, hasInitialPost]);

  const placeName = useMemo(() => (
    location.replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  ), [location]);

  const author = useMemo(() => {
    const p: any = post || {};
    const owner: any = p.owner || null;
    // Prefer numeric personId for URLs/CTAs; avoid GUID userId in routes
    const rawPersonId = (typeof owner?.personId === 'number' && owner.personId > 0)
      ? owner.personId
      : (typeof p.personId === 'number' && p.personId > 0)
        ? p.personId
        : null;
    const id: number | null = rawPersonId;
    // Scrubbed for every viewer, on the server copy and on the browser refetch alike.
    const stageNameRaw = owner?.displayName || p.stageName || p.authorName || p.userName || null;
    const stageName = stageNameRaw ? scrubContactInfo(String(stageNameRaw)) : null;
    const avatar = owner?.avatarUrl || p.avatarUrl || p.profileImageLocation || p.profileImageUrl || null;
    let profileUrl: string | null = p.profileUrl || p.authorProfileUrl || p.userProfileUrl || null;
    try {
      if (!profileUrl) {
        const sn = p.stageName || p.StageName || p.displayName || p.DisplayName || null;
        // A stage name that holds (or held) a phone number or email never becomes a slug: its
        // digits would land in the URL, and the masked name would point at another member.
        const clean = sn && !String(sn).includes(CONTACT_MASK) && scrubContactInfo(String(sn)) === String(sn);
        const sl = clean ? normalizeSlug(String(sn)) : null;
        profileUrl = (sl && sl.length >= 3) ? `/u/${sl}` : (id ? `/details/${id}` : null);
      }
    } catch {}
    const age = p.age ?? null;
    const sex = p.sex || p.gender || null;
    const ethnicity = p.ethnicity || p.race || null;
    const city = p.cityName || p.city || null;
    const zip = p.zipCode || p.zip || null;
    const state = p.state || p.stateCode || null;
    return { id, stageName, avatar, profileUrl, age, sex, ethnicity, city, zip, state } as {
      id: number | null; stageName: string | null; avatar: string | null; profileUrl: string | null; age: number | null; sex: string | null; ethnicity: string | null; city: string | null; zip: string | null | number; state: string | null;
    };
  }, [post]);

  // Normalize initial like state and like count from various backend shapes
  const { initialLiked, initialLikeCount } = useMemo(() => {
    const p: any = post || {};
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        if (typeof p[k] !== 'undefined') return p[k];
      }
      return undefined;
    };
    const toBool = (v: any): boolean => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
      }
      return false;
    };
    const likedRaw = pick('isLikedByMe','IsLikedByMe','likedByMe','LikedByMe','isLiked','IsLiked','liked','Liked');
    const countRaw = pick('likeCount','LikeCount','likesCount','LikesCount','likes','Likes');
    const liked = toBool(likedRaw);
    const likeCount = typeof countRaw === 'number' ? countRaw : (typeof countRaw === 'string' ? (Number(countRaw) || undefined) : undefined);
    return { initialLiked: liked, initialLikeCount: likeCount as number | undefined };
  }, [post]);

  const galleryImages: string[] = useMemo(() => {
    const p: any = post || {};
    const candidates: any[] = [];
    if (Array.isArray(p.images)) candidates.push(...p.images);
    if (Array.isArray(p.photos)) candidates.push(...p.photos);
    if (Array.isArray(p.gallery)) candidates.push(...p.gallery);
    if (Array.isArray(p.imageUrls)) candidates.push(...p.imageUrls);
    if (Array.isArray(p.media) && p.media.length) {
      for (const m of p.media) if (m?.url) candidates.push(m.url);
    }
    // Compare keys ignoring _avatar/_thumb variants so we can drop the poster's
    // profile pic when it shows up in the post's own image list (common on /api/post).
    const stripVariant = (s: string) => String(s || '').replace(/_(avatar|thumb)(\.[a-z0-9]+)$/i, '$2');
    const avatarKey = stripVariant(String(p.avatarUrl || p.AvatarUrl || p.owner?.avatarUrl || ''));
    return candidates
      .map((x) => (typeof x === 'string' ? x : x?.url || x?.src || null))
      .filter(Boolean)
      .map((u: string) => stripVariant(u))
      .filter((u: string) => !avatarKey || u !== avatarKey)
      .map((u: string) => toAbsoluteMediaUrl(u));
  }, [post]);

  // Only show a post image if the post actually has gallery/media images.
  // Falling back to thumbnailUrl/imageUrl was returning the poster's profile
  // avatar — already shown in the author card, so it'd be redundant.
  const primaryImage: string | null = useMemo(() => {
    return galleryImages && galleryImages.length > 0 ? (galleryImages[0] || null) : null;
  }, [galleryImages]);

  // The subject of this page IS a post, so a message sent from here is an
  // application to it, and that is the only thing postId on the wire means:
  // it is what dbo.UserComLog.PostId is counted on. Resolved the same way the
  // renew and deactivate calls above resolve it, and sent only when it is a
  // real id, so a junk value can never look like a gig application.
  const applicationPostId = useMemo(() => {
    const raw = post?.postId ?? post?.PostId ?? postId;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [post, postId]);

  async function onSendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!author.id) { setSendErr('Messaging unavailable for this post.'); return; }
    if (!msg.trim()) return;
    setSending(true);
    setSendErr(null);
    try {
      await sendMessage({ toId: String(author.id), message: msg.trim(), messageType: 'text', postId: applicationPostId });
      // NOTE: this inline form is unmounted; ProfileCTAs below is the live reply
      // surface, and both of its paths carry the post id already (the apref
      // path through ChatClient, the Email path through ContactController).
      // This send matches them so a remount cannot start writing a null PostId.
      setMsg('');
    } catch (err: any) {
      setSendErr(err?.message || 'Failed to send message');
    } finally { setSending(false); }
  }


  if (loading) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
        {/* Breadcrumb */}
        <div className="h-4 w-64 skeleton rounded" />

        {/* Single merged post card: eyebrow + title + body grid + reply CTA */}
        <div className="relative overflow-hidden rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg skeleton" />
            <div className="h-3 w-44 skeleton rounded" />
          </div>

          {/* Title (2 lines) */}
          <div className="mt-3 space-y-2">
            <div className="h-9 md:h-11 w-3/4 skeleton rounded" />
            <div className="h-9 md:h-11 w-1/2 skeleton rounded" />
          </div>

          {/* Body grid */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-8 space-y-3">
              <div className="h-4 w-11/12 skeleton rounded" />
              <div className="h-4 w-10/12 skeleton rounded" />
              <div className="h-4 w-9/12 skeleton rounded" />
              <div className="h-4 w-7/12 skeleton rounded" />
              <div className="h-4 w-1/2 skeleton rounded" />
              <div className="pt-2">
                <div className="h-6 w-44 skeleton rounded-full" />
              </div>
            </div>
            <aside className="lg:col-span-4">
              <div className="rounded-2xl border border-zinc-200/60 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 p-3 flex items-center gap-3">
                <div className="h-12 w-12 rounded-full skeleton shrink-0" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-4 w-28 skeleton rounded" />
                  <div className="h-3 w-20 skeleton rounded" />
                </div>
              </div>
            </aside>
          </div>

          {/* Reply CTA inside the card */}
          <div className="mt-8 text-center space-y-3">
            <div className="mx-auto h-7 w-56 skeleton rounded" />
            <div className="mx-auto h-3 w-72 skeleton rounded" />
            <div className="flex justify-center gap-2 pt-2">
              <div className="h-10 w-24 skeleton rounded-md" />
              <div className="h-10 w-24 skeleton rounded-md" />
            </div>
          </div>
        </div>

        {/* Like / Share / Report toolbar */}
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full skeleton" />
          <div className="h-9 w-9 rounded-full skeleton" />
          <div className="h-9 w-9 rounded-full skeleton" />
        </div>

        {/* "More in {place}" separator + 2 post-card placeholders */}
        <div className="my-2 flex items-center gap-3" aria-hidden>
          <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
          <div className="h-3 w-32 skeleton rounded" />
          <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
        </div>
        <div className="grid grid-cols-1 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <PostCardSkeleton key={i} variant="light" showMeta />
          ))}
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
        <nav className="text-sm text-zinc-500 dark:text-zinc-400" aria-label="Breadcrumb">
          <Link href="/posts" className="hover:underline">Posts</Link>
          <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
          <Link href={`/posts/${location}`} className="hover:underline">{placeName}</Link>
        </nav>
        <div className="rounded-3xl border border-zinc-200/60 bg-white p-10 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
          <h1 className="font-serif text-2xl font-semibold text-zinc-900 dark:text-zinc-50">Post not found</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">It may have been removed or never existed.</p>
          <div className="mt-5 flex justify-center">
            <Button asChild variant="outline">
              <Link href={`/posts/${location}/${category}`}><ChevronLeft className="h-4 w-4" aria-hidden /> Back to listings</Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Structured data: one Article (ArticleSchema, below) and the breadcrumbs.
  // No Event block: a post is not an event, and Google's test failed it on 9
  // of 48 post URLs (2026-10-16). URLs name the slug form, the post's one
  // canonical address.
  const site = (process.env.NEXT_PUBLIC_SITE_BASE_URL || '').replace(/\/$/, '');
  const postPageUrl = canonicalUrl
    || `${site}/posts/${encodeURIComponent(String(location))}/${encodeURIComponent(String(category))}/${encodeURIComponent(String(postId))}`;

  const crumbsJson = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${site}/` },
      { '@type': 'ListItem', position: 2, name: 'Posts', item: `${site}/posts` },
      { '@type': 'ListItem', position: 3, name: placeName, item: `${site}/posts/${encodeURIComponent(String(location))}` },
      { '@type': 'ListItem', position: 4, name: humanizeSlug(category), item: `${site}/posts/${encodeURIComponent(String(location))}/${encodeURIComponent(String(category))}` },
      { '@type': 'ListItem', position: 5, name: scrubContactInfo(String((post as any)?.title || `Post #${postId}`)), item: postPageUrl },
    ],
  } as any;

  // Deduplicate title from description/body if repeated
  const pageTitle = String((post as any)?.title || (post as any)?.name || `Post #${postId}`).trim();
  const normalize = (s: string) => s.replace(/\s+/g, ' ').replace(/[\-–—:_]+/g, ' ').trim().toLowerCase();
  const stripLeadingTitleText = (text?: string | null): string | null => {
    if (!text) return null;
    const t = String(text);
    const normTitle = normalize(pageTitle);
    // Remove exact first-line duplicate of the title
    const lines = t.split(/\r?\n/);
    if (lines.length && normalize(lines[0]) === normTitle) {
      return lines.slice(1).join('\n').trimStart();
    }
    return t;
  };
  const stripLeadingTitleHtml = (html?: string | null): string | null => {
    if (!html) return null;
    let s = String(html);
    try {
      // Remove leading H1/H2 that matches title (cheap regex; safe because only removing tags + content)
      s = s.replace(/<h[12][^>]*>(.*?)<\/h[12]>/i, (m, inner) => (normalize(String(inner)) === normalize(pageTitle) ? '' : m));
    } catch {}
    return s;
  };

  const safeDetails = stripLeadingTitleText((post as any)?.description || (post as any)?.details || null);
  const safeBody = stripLeadingTitleHtml((post as any)?.body || null);
  const categoryDesc: string | null = (() => {
    const p: any = post || {};
    const v = p.categoryDescription ?? p.CategoryDescription ?? p.category ?? p.Category ?? null;
    return v ? String(v) : null;
  })();

  const siteBase = (process.env.NEXT_PUBLIC_SITE_BASE_URL || 'https://juicystarz.com').replace(/\/$/, '');
  const postUrl = canonicalUrl || `${siteBase}/posts/${location}/${category}/${postId}`;
  const postTitle = post?.title || post?.Title || `Post #${postId}`;
  // Scrubbed after stripHtml, as live did, with ladders masked too: the whitespace collapse can turn
  // any wider separator (" -\r\n", markup inside the number) into a spaced-dash ladder. No scrub runs
  // before it, because a mask there can take the end of a number and leave its start readable.
  const postDescription = truncateDescription(
    scrubContactInfo(stripHtml(String(post?.description || post?.details || post?.body || '')), { maskLadders: true }),
    155
  );
  const postImage = post?.image || post?.imageUrl || (post?.images && post.images[0]) || undefined;
  // No invented date: a post without one gets no Article block and no date line.
  const datePosted: string | undefined = post?.DatePosted || post?.datePosted || post?.createdAt || undefined;
  const posted = postedDate(datePosted);
  const dateModified = post?.LastModified || post?.lastModified || post?.updatedAt || datePosted;

  if (postDeleted) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6 text-center space-y-4">
        <p className="text-lg font-medium">Post deleted.</p>
        <Link href="/posts" className="text-primary hover:underline text-sm">← Back to posts</Link>
      </main>
    );
  }

  // Resolve bucket theme via the SHARED resolver (lib/posts/labels): the old
  // local fallback regex predated the 2026-06-19 taxonomy, so e.g.
  // collab-f-duo showed COLLABORATE on its card but NOW HIRING here.
  const bucketKey = bucketForCategory(
    category,
    String((post as any)?.categoryType || (post as any)?.CategoryType || '')
    // A post with NO category at all still lands on hiring here. That is the
    // current rendering for a blank-category post and it is deliberately left
    // alone (PostDto.CategorySlug defaults to string.Empty; see the known-case
    // note). Changing it is a separate call, not a side effect of this one.
  ) || 'hiring';
  // Services, not hiring, for a key that is not in the map. BucketKey now has
  // exactly the four keys BUCKET_THEME has, so this is unreachable; it stays as
  // the safe default because services is the harmless thing to be wrong as and
  // "Now hiring" is not: it would tell a reader a post is a paid opening.
  const bucket = BUCKET_THEME[bucketKey] || BUCKET_THEME.services;
  const BucketIcon = bucket.Icon;
  const humanCategoryName = categoryName
    || ((post as any)?.categoryDescription && String((post as any).categoryDescription).trim())
    || category.replace(/-/g, ' ').replace(/\b\w/g, s => s.toUpperCase());

  return (
    <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8 space-y-6">
      {post && datePosted && (
        <ArticleSchema
          headline={scrubContactInfo(String(postTitle))}
          description={postDescription}
          datePublished={datePosted}
          dateModified={dateModified}
          author={{
            name: author.stageName || 'JuicyStarz User',
            url: author.profileUrl || undefined,
          }}
          image={postImage ? toAbsoluteMediaUrl(postImage) : undefined}
          url={postUrl}
          articleSection={post?.categoryDescription || post?.CategoryDescription || category}
        />
      )}

      {/* Breadcrumb */}
      <nav className="text-sm text-zinc-500 dark:text-zinc-400" aria-label="Breadcrumb">
        <Link href="/posts" className="hover:underline">Posts</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <Link href={`/posts/${location}`} className="hover:underline">{placeName}</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <Link href={`/posts/${location}/${category}`} className="hover:underline">{humanCategoryName}</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <span className="font-semibold text-zinc-900 dark:text-zinc-100">#{postId}</span>
      </nav>

      {isAdmin && (
        <div className="flex items-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 px-3 py-2 text-sm">
          <span className="font-semibold text-orange-700 dark:text-orange-400 mr-2">Admin:</span>
          <a href={`/admin/posts`} className="rounded border px-2 py-1 text-xs hover:bg-orange-100 dark:hover:bg-orange-900">View in Admin</a>
          <button disabled={adminBusy} onClick={adminDeactivate} className="rounded border px-2 py-1 text-xs hover:bg-orange-100 dark:hover:bg-orange-900 disabled:opacity-50">Deactivate</button>
          <button disabled={adminBusy} onClick={adminDelete} className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 disabled:opacity-50">Delete</button>
          {adminMsg && (
            <span className={`ml-2 text-xs ${adminMsg.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>{adminMsg.text}</span>
          )}
        </div>
      )}

      {/* Combined post card — single continuous card, eyebrow + title flow into body + author */}
      <article className="relative overflow-hidden rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
        <div aria-hidden className={cn('pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl', bucket.glow)} />
        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2">
            <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-lg', bucket.iconBg)}>
              <BucketIcon className={cn('h-4 w-4', bucket.iconColor)} />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              {bucket.label} · {humanCategoryName}
            </span>
          </div>
          <h1 className="font-serif text-[clamp(1.75rem,5vw,2.75rem)] font-semibold leading-[1.1] tracking-tight text-zinc-900 dark:text-zinc-50">
            {scrubContactInfo(postTitle)}
          </h1>

          {/* Owner-facing hold status. Only the owner/admin ever receives a
              non-published post from the API (everyone else 404s), so
              isPublished === false here always means "you are looking at your
              own held post". Without this banner authors were redirected to a
              normally-rendered page and never knew the post was invisible. */}
          {post?.isPublished === false && (
            <div role="status" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
              {/* isPublished=false covers PendingReview AND Rejected/Hidden, so the
                  copy must not promise publication. */}
              <strong>Not publicly visible.</strong> Only you can see this post right now. If it is awaiting review, you will get a notification when it goes live.
            </div>
          )}

          {/* Owner-only renew strip. Renewing a held/hidden post does nothing
              publicly, so this only shows on published posts. */}
          {isOwner && post?.isPublished !== false && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200/60 bg-zinc-50 px-4 py-2.5 text-sm dark:border-zinc-800 dark:bg-zinc-900">
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Your post</span>
              <Button variant="outline" size="sm" onClick={renewPost} disabled={renewBusy} className="h-7 px-3 text-xs">
                {renewBusy ? 'Renewing…' : 'Renew'}
              </Button>
              {renewNote ? (
                <span className={`text-xs ${renewNote.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>{renewNote.text}</span>
              ) : (
                <span className="text-xs text-zinc-500 dark:text-zinc-400">Renewing moves this post back to the top of its listings.</span>
              )}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-8 space-y-4 min-w-0">
              {safeDetails ? (
                <div className="prose dark:prose-invert max-w-none">
                  <p
                    className="text-base md:text-lg leading-7 md:leading-8 text-zinc-700 dark:text-zinc-200 whitespace-pre-line"
                    dangerouslySetInnerHTML={{ __html: scrubContactInfo(linkify(safeDetails)) }}
                  />
                </div>
              ) : null}
              {safeBody && (
                <div className="prose prose-lg dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: scrubContactInfo(sanitizeHtml(safeBody)) }} />
              )}
              {(author.city || author.state || author.zip || posted) && (
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  {(author.city || author.state || author.zip) && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200">
                      <MapPin className="h-3.5 w-3.5 opacity-80" aria-hidden />
                      {/* author.city often holds the metro slug ("los-angeles") */}
                      <span>{[friendlyCity(author.city), author.state, author.zip].filter(Boolean).join(', ')}</span>
                    </span>
                  )}
                  {posted && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200">
                      <CalendarDays className="h-3.5 w-3.5 opacity-80" aria-hidden />
                      <span>Posted <time dateTime={posted.iso}>{posted.label}</time></span>
                    </span>
                  )}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-3">
              {author.id && (
                <MiniTalentCard
                  id={author.id as any}
                  name={author.stageName || undefined as any}
                  avatarUrl={author.avatar || undefined as any}
                  profileUrl={(author.profileUrl || (normalizeSlug(author.stageName || '') ? `/u/${normalizeSlug(author.stageName || '')}` : (author.id ? `/details/${encodeURIComponent(String(author.id))}` : undefined))) as any}
                />
              )}
            </aside>
          </div>

          {/* Reply CTA — folded into the same card so the post is a single unit */}
          {author.id && (
            <div className="mt-8 text-center">
              <h2 className="font-serif text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
                Reply to this post
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-zinc-600 dark:text-zinc-300">
                Message the poster on-platform to discuss the work. Keep contact and bookings on JuicyStarz.
              </p>
              <div className="mt-5 flex justify-center">
                <ProfileCTAs
                  personId={author.id as any}
                  variant="brand"
                  size="lg"
                  layout="inline"
                  context="post"
                  contextId={postId}
                  className="justify-center gap-3"
                />
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Like/Share/Report toolbar */}
      <DetailToolbar
        type="post"
        id={Number(postId)}
        title={postTitle}
        author={{ id: author.id || undefined as any, name: author.stageName || undefined as any, avatarUrl: author.avatar || undefined as any, profileUrl: author.profileUrl || undefined as any }}
        liked={initialLiked}
        likeable
        share
        report
        juicyTitle={false}
        variant="banner"
      />

      <CommentsPanelClient id={Number(postId)} resource="post" />

      {related && related.length > 0 && (
        <>
          <div className="my-8 flex items-center gap-3" aria-hidden>
            <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
              More in {placeName}
            </span>
            <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
          </div>
          <section className="grid grid-cols-1 gap-4" aria-label={`More posts in ${placeName}`}>
            {related.map(p => (
              <PostCard key={p.postId} {...p} showMeta variant="light" showCategoryRibbon />
            ))}
          </section>
        </>
      )}

      <Dialog open={!!lightboxSrc} onOpenChange={(o) => { if (!o) setLightboxSrc(null); }}>
        <DialogContent className="p-0 max-w-4xl bg-background">
          <DialogHeader className="px-4 pt-4 pb-0">
            <DialogTitle>Image</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {lightboxSrc && (
              <div className="relative w-full aspect-[4/3]">
                <Image src={lightboxSrc} alt="View" fill sizes="80vw" className="object-contain rounded-md" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Structured data: BreadcrumbList (the one Article block is ArticleSchema, above) */}
      <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: jsonLdHtml(crumbsJson) }} />
    </main>
  );
}



